import config from "../../config";
import { getlist, post } from "../../contexts/apicontexts";

const normalizeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeString = (value, fallback = "") => {
  const text = String(value ?? "").trim();
  return text || fallback;
};

const pad2 = (value) => String(value).padStart(2, "0");

const normalizeOption = (item, overrides = {}) => ({
  id: normalizeNumber(item?.id ?? item?.Id),
  name: normalizeString(item?.name ?? item?.Name),
  ...overrides,
});

const normalizeOrganization = (item) =>
  normalizeOption(item, {
    organizationCode: normalizeString(item?.organizationCode ?? item?.OrganizationCode),
  });

const normalizeCustomer = (item) =>
  normalizeOption(item, {
    customerCode: normalizeString(item?.customerCode ?? item?.CustomerCode),
    organizationId: normalizeNumber(item?.organizationId ?? item?.OrganizationId),
    organizationName: normalizeString(item?.organizationName ?? item?.OrganizationName),
    empUserId: normalizeString(item?.empUserId ?? item?.EmpUserId),
  });

const normalizeCustomerUser = (item) => ({
  id: normalizeNumber(item?.id ?? item?.Id),
  customerId: normalizeNumber(item?.customerId ?? item?.CustomerId),
  userId: normalizeString(item?.userId ?? item?.UserId),
  fullName: normalizeString(item?.fullName ?? item?.FullName ?? item?.name ?? item?.Name, "Unknown User"),
  email: normalizeString(item?.email ?? item?.Email),
});

const normalizeEmployee = (item) => ({
  id: normalizeNumber(item?.id ?? item?.Id),
  userId: normalizeString(item?.userId ?? item?.UserId ?? item?.userID ?? item?.UserID),
  fullName: normalizeString(item?.fullName ?? item?.FullName ?? item?.name ?? item?.Name, "Employee"),
  employeeCode: normalizeString(item?.employeeCode ?? item?.EmployeeCode),
  employeeNumber: normalizeString(item?.employeeNumber ?? item?.EmployeeNumber),
  workEmail: normalizeString(item?.workEmail ?? item?.WorkEmail ?? item?.email ?? item?.Email),
});

const normalizeMetricPoint = (item, labelKey = "label", valueKey = "value") => ({
  label: normalizeString(item?.[labelKey] ?? item?.Label, "Unknown"),
  value: normalizeNumber(item?.[valueKey] ?? item?.Value),
});

const normalizeStatusBreakdown = (item) => ({
  label: normalizeString(item?.label ?? item?.Label, "Unknown"),
  value: normalizeNumber(item?.value ?? item?.Value),
  color: normalizeString(item?.color ?? item?.Color),
});

const normalizeTrendPoint = (item, valueField) => ({
  label: normalizeString(item?.label ?? item?.Label),
  date: item?.date ?? item?.Date ?? null,
  value: normalizeNumber(item?.[valueField] ?? item?.Value),
});

const normalizeEmployeeWorkload = (item) => ({
  employeeUserId: normalizeString(item?.employeeUserId ?? item?.EmployeeUserId),
  employeeName: normalizeString(item?.employeeName ?? item?.EmployeeName, "Unassigned"),
  employeeEmail: normalizeString(item?.employeeEmail ?? item?.EmployeeEmail),
  customerCount: normalizeNumber(item?.customerCount ?? item?.CustomerCount),
  ticketCount: normalizeNumber(item?.ticketCount ?? item?.TicketCount),
  taskCount: normalizeNumber(item?.taskCount ?? item?.TaskCount),
});

const normalizeActiveCustomerUser = (item) => ({
  customerUserId: normalizeString(item?.customerUserId ?? item?.CustomerUserId),
  customerUserName: normalizeString(item?.customerUserName ?? item?.CustomerUserName, "Unknown User"),
  customerName: normalizeString(item?.customerName ?? item?.CustomerName, "Unknown Customer"),
  ticketCount: normalizeNumber(item?.ticketCount ?? item?.TicketCount),
  taskCount: normalizeNumber(item?.taskCount ?? item?.TaskCount),
  lastActivityAt: item?.lastActivityAt ?? item?.LastActivityAt ?? null,
});

const normalizeCustomerResolution = (item) => ({
  customerId: normalizeNumber(item?.customerId ?? item?.CustomerId),
  customerName: normalizeString(item?.customerName ?? item?.CustomerName, "Unknown Customer"),
  organizationName: normalizeString(item?.organizationName ?? item?.OrganizationName),
  ticketCount: normalizeNumber(item?.ticketCount ?? item?.TicketCount),
  resolvedTicketCount: normalizeNumber(item?.resolvedTicketCount ?? item?.ResolvedTicketCount),
  openTicketCount: normalizeNumber(item?.openTicketCount ?? item?.OpenTicketCount),
  resolutionRate: normalizeNumber(item?.resolutionRate ?? item?.ResolutionRate),
  averageResolutionHours: normalizeNumber(item?.averageResolutionHours ?? item?.AverageResolutionHours),
});

const normalizeTimelineItem = (item) => ({
  type: normalizeString(item?.type ?? item?.Type, "Activity"),
  title: normalizeString(item?.title ?? item?.Title, "Activity update"),
  subtitle: normalizeString(item?.subtitle ?? item?.Subtitle),
  status: normalizeString(item?.status ?? item?.Status),
  occurredAt: item?.occurredAt ?? item?.OccurredAt ?? null,
  actorName: normalizeString(item?.actorName ?? item?.ActorName),
});

const normalizeAnalyticsResponse = (data) => ({
  summary: {
    totalOrganizations: normalizeNumber(data?.summary?.totalOrganizations ?? data?.Summary?.TotalOrganizations),
    totalCustomers: normalizeNumber(data?.summary?.totalCustomers ?? data?.Summary?.TotalCustomers),
    totalCustomerUsers: normalizeNumber(data?.summary?.totalCustomerUsers ?? data?.Summary?.TotalCustomerUsers),
    totalEmployees: normalizeNumber(data?.summary?.totalEmployees ?? data?.Summary?.TotalEmployees),
    totalTickets: normalizeNumber(data?.summary?.totalTickets ?? data?.Summary?.TotalTickets),
    totalTasks: normalizeNumber(data?.summary?.totalTasks ?? data?.Summary?.TotalTasks),
    openTickets: normalizeNumber(data?.summary?.openTickets ?? data?.Summary?.OpenTickets),
    resolvedTickets: normalizeNumber(data?.summary?.resolvedTickets ?? data?.Summary?.ResolvedTickets),
    resolutionRate: normalizeNumber(data?.summary?.resolutionRate ?? data?.Summary?.ResolutionRate),
    averageResolutionHours: normalizeNumber(data?.summary?.averageResolutionHours ?? data?.Summary?.AverageResolutionHours),
    averageTasksPerTicket: normalizeNumber(data?.summary?.averageTasksPerTicket ?? data?.Summary?.AverageTasksPerTicket),
    activeAssignments: normalizeNumber(data?.summary?.activeAssignments ?? data?.Summary?.ActiveAssignments),
  },
  statusBreakdown: Array.isArray(data?.statusBreakdown ?? data?.StatusBreakdown)
    ? (data?.statusBreakdown ?? data?.StatusBreakdown).map(normalizeStatusBreakdown)
    : [],
  ticketTrend: Array.isArray(data?.ticketTrend ?? data?.TicketTrend)
    ? (data?.ticketTrend ?? data?.TicketTrend).map((item) => normalizeTrendPoint(item, "tickets"))
    : [],
  taskTrend: Array.isArray(data?.taskTrend ?? data?.TaskTrend)
    ? (data?.taskTrend ?? data?.TaskTrend).map((item) => normalizeTrendPoint(item, "tasks"))
    : [],
  ticketsByCustomer: Array.isArray(data?.ticketsByCustomer ?? data?.TicketsByCustomer)
    ? (data?.ticketsByCustomer ?? data?.TicketsByCustomer).map((item) => normalizeMetricPoint(item, "label", "value"))
    : [],
  tasksByCustomer: Array.isArray(data?.tasksByCustomer ?? data?.TasksByCustomer)
    ? (data?.tasksByCustomer ?? data?.TasksByCustomer).map((item) => normalizeMetricPoint(item, "label", "value"))
    : [],
  ticketsByOrganization: Array.isArray(data?.ticketsByOrganization ?? data?.TicketsByOrganization)
    ? (data?.ticketsByOrganization ?? data?.TicketsByOrganization).map((item) => normalizeMetricPoint(item, "label", "value"))
    : [],
  employeeWorkload: Array.isArray(data?.employeeWorkload ?? data?.EmployeeWorkload)
    ? (data?.employeeWorkload ?? data?.EmployeeWorkload).map(normalizeEmployeeWorkload)
    : [],
  activeCustomerUsers: Array.isArray(data?.activeCustomerUsers ?? data?.ActiveCustomerUsers)
    ? (data?.activeCustomerUsers ?? data?.ActiveCustomerUsers).map(normalizeActiveCustomerUser)
    : [],
  customerResolution: Array.isArray(data?.customerResolution ?? data?.CustomerResolution)
    ? (data?.customerResolution ?? data?.CustomerResolution).map(normalizeCustomerResolution)
    : [],
  activityTimeline: Array.isArray(data?.activityTimeline ?? data?.ActivityTimeline)
    ? (data?.activityTimeline ?? data?.ActivityTimeline).map(normalizeTimelineItem)
    : [],
  satisfaction: {
    available: Boolean(data?.satisfaction?.available ?? data?.Satisfaction?.Available),
    score: normalizeNumber(data?.satisfaction?.score ?? data?.Satisfaction?.Score, 0),
    label: normalizeString(data?.satisfaction?.label ?? data?.Satisfaction?.Label, "Not available"),
  },
});

export const getDefaultAnalyticsFilters = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 29);

  return {
    organizationId: "",
    customerId: "",
    customerUserId: "",
    employeeUserId: "",
    status: "All",
    startDate: formatAnalyticsDisplayDate(start),
    endDate: formatAnalyticsDisplayDate(end),
  };
};

export const formatAnalyticsDisplayDate = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${pad2(date.getDate())}-${pad2(date.getMonth() + 1)}-${date.getFullYear()}`;
};

export const parseAnalyticsDisplayDate = (value) => {
  const text = normalizeString(value);
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(text);
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);

  if (!day || !month || !year) return null;

  const date = new Date(year, month - 1, day);
  if (
    Number.isNaN(date.getTime())
    || date.getFullYear() !== year
    || date.getMonth() !== month - 1
    || date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

export const toAnalyticsApiDate = (value) => {
  const date = parseAnalyticsDisplayDate(value);
  if (!date) return null;
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

export const fetchAnalyticsOrganizations = async () => {
  const response = await getlist(`${config.baseurl}Organizations/getlist`);
  if (response?.status !== 200 || !Array.isArray(response.data)) return [];
  return response.data.map(normalizeOrganization).filter((item) => item.id > 0 && item.name);
};

export const fetchAnalyticsCustomers = async () => {
  const response = await getlist(`${config.baseurl}Customer/getlist`);
  if (response?.status !== 200 || !Array.isArray(response.data)) return [];
  return response.data.map(normalizeCustomer).filter((item) => item.id > 0 && item.name);
};

export const fetchAnalyticsEmployees = async () => {
  const response = await getlist(`${config.baseurl}Employee/getlist`);
  if (response?.status !== 200 || !Array.isArray(response.data)) return [];
  return response.data
    .map(normalizeEmployee)
    .filter((item) => (item.id > 0 || item.userId) && item.fullName);
};

export const fetchAnalyticsCustomerUsers = async (customerIds = []) => {
  const uniqueIds = Array.from(new Set((customerIds || []).map((item) => normalizeNumber(item)).filter((item) => item > 0)));
  if (!uniqueIds.length) return [];

  const responses = await Promise.allSettled(
    uniqueIds.map((customerId) => getlist(`${config.baseurl}CustomerUser/getlist?customerId=${encodeURIComponent(customerId)}`))
  );

  return Array.from(
    new Map(
      responses
        .filter((result) => result.status === "fulfilled" && result.value?.status === 200 && Array.isArray(result.value.data))
        .flatMap((result) => result.value.data.map(normalizeCustomerUser))
        .map((item) => [`${item.id}-${item.userId}`, item])
    ).values()
  );
};

export const fetchAnalyticsStatusOptions = async () => {
  const response = await getlist(`${config.baseurl}TicketAnalyticsReport/status-options`);
  if (response?.status === 200 && Array.isArray(response.data)) {
    return ["All", ...response.data.map((item) => normalizeString(item)).filter(Boolean)];
  }

  return ["All", "New", "In Progress", "On Hold", "Resolved"];
};

export const fetchTicketAnalyticsDashboard = async (filters) => {
  const payload = {
    organizationId: normalizeNumber(filters?.organizationId) || null,
    customerId: normalizeNumber(filters?.customerId) || null,
    customerUserId: normalizeString(filters?.customerUserId) || null,
    employeeUserId: normalizeString(filters?.employeeUserId) || null,
    status: normalizeString(filters?.status) && filters.status !== "All" ? normalizeString(filters.status) : null,
    startDate: toAnalyticsApiDate(filters?.startDate),
    endDate: toAnalyticsApiDate(filters?.endDate),
  };

  const response = await post(`${config.baseurl}TicketAnalyticsReport/dashboard`, payload);
  if (response?.status === 200) {
    return normalizeAnalyticsResponse(response.data || {});
  }

  const message = response?.message || response?.error || "Analytics dashboard could not be loaded.";
  throw new Error(message);
};
